import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import MisApps from "@/pages/MisApps";
import { inicioPiloto } from "@/lib/inicio";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Productos from "@/pages/Productos";
import Envios from "@/pages/Envios";
import Transportistas from "@/pages/Transportistas";
import Cajas from "@/pages/Cajas";
import Modificadores from "@/pages/Modificadores";
import CxCFacturada from "@/pages/CxCFacturada";
import FotosPagos from "@/pages/FotosPagos";
import FacturasGastos from "@/pages/FacturasGastos";
import PedidosCancelados from "@/pages/PedidosCancelados";
import FlujoFacturados from "@/pages/FlujoFacturados";
import HistorialCierresRuta from "@/pages/HistorialCierresRuta";
import ReporteVisual from "@/pages/ReporteVisual";
import MetodosPago from "@/pages/MetodosPago";
import Conciliacion from "@/pages/Conciliacion";
import Clientes from "@/pages/Clientes";
import Pedidos from "@/pages/Pedidos";
import PedidoDetalle from "@/pages/PedidoDetalle";
import Fabricacion from "@/pages/Fabricacion";
import Empaque from "@/pages/Empaque";
import Entrega from "@/pages/Entrega";
import Facturacion from "@/pages/Facturacion";
import Usuarios from "@/pages/Usuarios";
import Reportes from "@/pages/Reportes";
import Notificaciones from "@/pages/Notificaciones";
import GuiaDespacho from "@/pages/GuiaDespacho";
import HojaFabricacion from "@/pages/HojaFabricacion";
import Costuras from "@/pages/Costuras";
import Bases from "@/pages/Bases";
import Alertas from "@/pages/Alertas";
import MisEntregas from "@/pages/MisEntregas";
import MisVentas from "@/pages/MisVentas";
import Promociones from "@/pages/Promociones";
import SeguimientoEnvio from "@/pages/SeguimientoEnvio";
import HistorialReportes from "@/pages/HistorialReportes";
import HistorialEntregas from "@/pages/HistorialEntregas";
import CierreAutomatico from "@/pages/CierreAutomatico";
import Marcaje from "@/pages/Marcaje";
import Gastos from "@/pages/Gastos";
import Cuentas from "@/pages/Cuentas";
import ResumenPagos from "@/pages/ResumenPagos";
import ReporteRuta from "@/pages/ReporteRuta";
import AppDomicilios from "@/pages/AppDomicilios";
import AppEmpaque from "@/pages/AppEmpaque";
import OrganizadorPaquetes from "@/pages/OrganizadorPaquetes";
import AppColocacion from "@/pages/AppColocacion";
import AppSeguimiento from "@/pages/AppSeguimiento";
import CuadreRepartidor from "@/pages/CuadreRepartidor";
import RutaMapa from "@/pages/RutaMapa";
import CierreMes from "@/pages/CierreMes";
import Inventario from "@/pages/Inventario";
import PlataformaGuias from "@/pages/PlataformaGuias";
import LandingAdmin from "@/pages/LandingAdmin";
import LandingAnalytics from "@/pages/LandingAnalytics";
import ReporteVendedoras from "@/pages/ReporteVendedoras";
// --- Public storefront ---
import PublicLayout from "@/pages/public/PublicLayout";
import LandingPublic from "@/pages/public/LandingPublic";
import Tienda from "@/pages/public/Tienda";
import ProductoPublico from "@/pages/public/ProductoPublico";
import Carrito from "@/pages/public/Carrito";
import PublicErrorBoundary from "@/components/PublicErrorBoundary";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50">
        <div className="text-sm text-zinc-500">Cargando...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Mapa ruta → módulo. Se usa para redirigir a la primera página permitida y
// para bloquear acceso por URL directa cuando el usuario tiene `modulos`
// custom (lista blanca de permisos individuales).
const ROUTE_MODULO = {
  "/panel": "dashboard", "/pedidos": "pedidos", "/mis-ventas": "pedidos",
  "/productos": "productos", "/promociones": "productos", "/modificadores": "productos",
  "/envios": "cuentas", "/transportistas": "cuentas", "/cajas": "cuentas",
  "/metodos-pago": "cuentas", "/cuentas": "cuentas",
  "/conciliacion": "conciliacion", "/cierre-automatico": "conciliacion",
  "/cxc-facturada": "reportes", "/reporte-visual": "reportes",
  "/fotos-pagos": "reportes", "/pedidos-cancelados": "reportes",
  "/reporte-ruta": "reportes", "/ruta-mapa": "reportes",
  "/reporte-empaque": "empaque", "/app-empaque": "empaque", "/organizador-paquetes": "organizador", "/app-colocacion": "colocacion", "/app-seguimiento": "seguimiento", "/historial-reportes": "reportes",
  "/resumen-pagos": "reportes", "/reportes": "reportes",
  "/reporte-vendedoras": "reportes", "/notificaciones": "dashboard",
  "/gastos": "gastos", "/facturas-gastos": "gastos",
  "/flujo-facturados": "facturacion", "/facturacion": "facturacion",
  "/fabricacion": "fabricacion", "/costuras": "costuras", "/bases": "bases", "/alertas": "alertas", "/empaque": "empaque",
  "/entrega": "entregas", "/historial-entregas": "entregas", "/mis-entregas": "entregas",
  "/domicilios": "domicilios", "/cuadre-repartidor": "cuadre",
  "/cierre-mes": "cierre_mes", "/marcaje": "marcaje",
  "/seguimiento": "seguimiento", "/inventario": "inventario",
  "/plataforma-guias": "plataforma_guias",
  "/landing-admin": "landing", "/landing-analytics": "landing",
  "/clientes": "clientes", "/usuarios": "usuarios",
};

function moduloDeRuta(pathname) {
  // Match exact primero, luego prefijos con parámetros.
  if (ROUTE_MODULO[pathname]) return ROUTE_MODULO[pathname];
  for (const p of Object.keys(ROUTE_MODULO)) {
    if (pathname.startsWith(p + "/")) return ROUTE_MODULO[p];
  }
  return null;
}

// Guard por módulo. Si el usuario tiene `modulos` custom y esta ruta requiere
// un módulo que NO está en su lista, lo redirige a la primera ruta permitida.
function RequireModulo({ children }) {
  const { user } = useAuth();
  const { pathname } = window.location;
  if (user?.role === "admin") return children;
  const modulos = user?.modulos;
  if (!Array.isArray(modulos) || modulos.length === 0) return children;
  const need = moduloDeRuta(pathname);
  if (!need) return children;
  if (modulos.includes(need)) return children;
  if (user?.role === "piloto") return <Navigate to={inicioPiloto(user)} replace />;
  // Redirigir a la primera ruta que sí tenga acceso.
  const first = Object.entries(ROUTE_MODULO).find(([, m]) => modulos.includes(m));
  return <Navigate to={first ? first[0] : "/login"} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/login" element={<Login />} />

              {/* Rutas públicas de la tienda (siempre accesibles, sin login) */}
              <Route element={<PublicErrorBoundary><PublicLayout /></PublicErrorBoundary>}>
                <Route path="/" element={<LandingPublic />} />
                <Route path="/producto/:id" element={<ProductoPublico />} />
                <Route path="/carrito" element={<Carrito />} />
                <Route path="/tienda" element={<Navigate to="/" replace />} />
              </Route>

              {/* APP DOMICILIOS - vista mobile-first sin layout admin, protegida por login */}
              <Route path="/domicilios" element={<Protected><RequireModulo><AppDomicilios /></RequireModulo></Protected>} />
              <Route path="/mis-apps" element={<Protected><MisApps /></Protected>} />
              <Route path="/app-empaque" element={<Protected><RequireModulo><AppEmpaque /></RequireModulo></Protected>} />
              <Route path="/organizador-paquetes" element={<Protected><RequireModulo><OrganizadorPaquetes /></RequireModulo></Protected>} />
              <Route path="/app-colocacion" element={<Protected><RequireModulo><AppColocacion /></RequireModulo></Protected>} />
              <Route path="/app-seguimiento" element={<Protected><RequireModulo><AppSeguimiento /></RequireModulo></Protected>} />

              {/* Sistema interno (requiere login + módulo autorizado) */}
              <Route element={<Protected><RequireModulo><Layout /></RequireModulo></Protected>}>
                <Route path="/panel" element={<Dashboard />} />
                <Route path="/pedidos" element={<Pedidos />} />
                <Route path="/pedidos/:id" element={<PedidoDetalle />} />
                <Route path="/productos" element={<Productos />} />
                <Route path="/envios" element={<Envios />} />
                <Route path="/transportistas" element={<Transportistas />} />
                <Route path="/cajas" element={<Cajas />} />
                <Route path="/modificadores" element={<Modificadores />} />
                <Route path="/cxc-facturada" element={<CxCFacturada />} />
                <Route path="/fotos-pagos" element={<FotosPagos />} />
                <Route path="/facturas-gastos" element={<FacturasGastos />} />
                <Route path="/pedidos-cancelados" element={<PedidosCancelados />} />
                <Route path="/flujo-facturados" element={<FlujoFacturados />} />
                <Route path="/reporte-ruta/historial" element={<HistorialCierresRuta />} />
                <Route path="/reporte-ruta/historial/:cid" element={<HistorialCierresRuta />} />
                <Route path="/reporte-visual" element={<ReporteVisual />} />
                <Route path="/metodos-pago" element={<MetodosPago />} />
                <Route path="/conciliacion" element={<Conciliacion />} />
                <Route path="/clientes" element={<Clientes />} />
                <Route path="/fabricacion" element={<Fabricacion />} />
                <Route path="/fabricacion/imprimir" element={<HojaFabricacion />} />
                <Route path="/costuras" element={<Costuras />} />
                <Route path="/bases" element={<Bases />} />
                <Route path="/alertas" element={<Alertas />} />
                <Route path="/empaque" element={<Empaque />} />
                <Route path="/entrega" element={<Entrega />} />
                <Route path="/seguimiento" element={<SeguimientoEnvio />} />
                <Route path="/historial-reportes" element={<HistorialReportes />} />
                <Route path="/historial-entregas" element={<HistorialEntregas />} />
                <Route path="/cierre-automatico" element={<CierreAutomatico />} />
                <Route path="/marcaje" element={<Marcaje />} />
                <Route path="/gastos" element={<Gastos />} />
                <Route path="/cuentas" element={<Cuentas />} />
                <Route path="/resumen-pagos" element={<ResumenPagos />} />
                <Route path="/reporte-ruta" element={<ReporteRuta />} />
                <Route path="/cuadre-repartidor" element={<CuadreRepartidor />} />
                <Route path="/ruta-mapa" element={<RutaMapa />} />
                <Route path="/cierre-mes" element={<CierreMes />} />
                
                <Route path="/reporte-empaque" element={<Navigate to="/app-empaque" replace />} />
                <Route path="/inventario" element={<Inventario />} />
                <Route path="/plataforma-guias" element={<PlataformaGuias />} />
                <Route path="/landing-admin" element={<LandingAdmin />} />
                <Route path="/landing-analytics" element={<LandingAnalytics />} />
                <Route path="/mis-entregas" element={<MisEntregas />} />
                <Route path="/mis-ventas" element={<MisVentas />} />
                <Route path="/facturacion" element={<Facturacion />} />
                <Route path="/usuarios" element={<Usuarios />} />
                <Route path="/promociones" element={<Promociones />} />
                <Route path="/reportes" element={<Reportes />} />
                <Route path="/reporte-vendedoras" element={<ReporteVendedoras />} />
                <Route path="/notificaciones" element={<Notificaciones />} />
                <Route path="/pedidos/:id/guia" element={<GuiaDespacho />} />
              </Route>
            </Routes>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
